// Design agent: MasqIP is meant to teach subnetting, not just compute it. This helper turns a single
// octet (0-255) into the building blocks a learner draws by hand on paper: the eight bits, the power
// of two each one represents, and which of those powers actually contributed to the final value.
// Developer agent: Kept dependency-free (no ipMath imports) so it can be unit tested in isolation and
// reused by any future component that needs a bit-level view of a byte, not only BinaryBreakdown.js.

// Design agent: Decomposes one octet into its eight bit descriptors, ordered most-significant-bit
// first (bit 7 / 128 down to bit 0 / 1) so callers can render them left-to-right exactly as they are
// written by hand.
// Developer agent: Throws on out-of-range input rather than silently clamping, so a bad caller is
// caught during development instead of rendering a misleading breakdown.
export function decomposeOctet(value) {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0 || value > 255) {
    throw new RangeError(`decomposeOctet expects an integer between 0 and 255, received: ${value}`);
  }

  const bits = [];
  for (let bitIndex = 7; bitIndex >= 0; bitIndex -= 1) {
    const power = 2 ** bitIndex;
    // Design agent: Bitwise AND against the power of two isolates whether that single bit is set,
    // mirroring the manual "is 128 part of 192?" question a learner asks for each column.
    const isSet = (value & power) === power;
    bits.push({
      bit: bitIndex,
      power,
      weight: isSet ? power : 0,
      isSet,
    });
  }
  return bits;
}

// Design agent: Builds the "128 + 64 = 192" style equation shown under each octet row, listing only
// the powers of two that are actually set so the reader sees exactly which columns were summed.
// Developer agent: Falls back to "0 = 0" when no bit is set, keeping the equation format consistent
// instead of rendering an empty string.
export function buildOctetEquation(octetValue) {
  const bits = decomposeOctet(octetValue);
  const setBits = bits.filter((entry) => entry.isSet);
  if (setBits.length === 0) {
    return `0 = ${octetValue}`;
  }
  const sum = setBits.map((entry) => entry.power).join(' + ');
  return `${sum} = ${octetValue}`;
}

// Design agent: Same equation as buildOctetEquation, but broken into addressable pieces so the UI
// can colour each decimal term with the colour of the power of two it comes from ("128" tinted like
// the "2^7" label printed above the bit). A plain string could not carry that mapping.
// Developer agent: Returns { terms, total }. `terms` is empty for an all-zero octet; callers render
// a single literal "0" in that case, matching the "0 = 0" wording of buildOctetEquation.
export function buildOctetEquationTerms(octetValue) {
  const bits = decomposeOctet(octetValue);
  const terms = bits
    .filter((entry) => entry.isSet)
    .map((entry) => ({ bit: entry.bit, power: entry.power }));
  return { terms, total: octetValue };
}

// Design agent: Tells the caller, for a given bit position inside a given octet, whether that bit
// belongs to the network portion or the host portion of the address — the same colour split already
// used elsewhere in the inspector (network vs host highlighting).
// Developer agent: `bitsMsbFirst` mirrors the order returned by decomposeOctet (index 0 = bit 7) so
// the two arrays can be zipped together directly when rendering.
export function getOctetBitRoles(octetIndex, prefix) {
  const roles = [];
  for (let indexInOctet = 0; indexInOctet < 8; indexInOctet += 1) {
    const globalBitPosition = octetIndex * 8 + indexInOctet;
    roles.push(globalBitPosition < prefix ? 'network' : 'host');
  }
  return roles;
}

// Design agent: Produces the dotted binary representation of a full IPv4 address
// ("11000000.10101000.00000001.00000001"), used by the popover's "Copy binary" action.
// Developer agent: Reuses decomposeOctet per octet so the copied string always matches what is
// rendered on screen, avoiding a second, divergent conversion path.
export function buildDottedBinary(address) {
  return address
    .split('.')
    .map((octetText) => {
      const octetValue = Number(octetText);
      const bits = decomposeOctet(octetValue);
      return bits.map((entry) => (entry.isSet ? '1' : '0')).join('');
    })
    .join('.');
}
